import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useAppSelector } from '@redux/hooks';
import { selectMemberDetails } from '@redux/teams';
import { ApiStatus } from '@constants';
import { logEvent } from '@services';
import { PageTitle } from 'constants/pageTitle';
import { useEffect } from 'react';
import { useTitle } from '@hooks';
import { selectPermissions } from '@redux/accounts';
import { hasPermission } from '@constants';
import { EvaluationTile } from '@components';

const tileBoxSx = {
  width: { xs: '100%', tablet: 220 },
  minHeight: { tablet: 120 },
};

export const MemberEvaluations = () => {
  const memberDetails = useAppSelector(selectMemberDetails);
  const permissions = useAppSelector(selectPermissions);
  const evaluations = memberDetails.data?.member.evaluations ?? [];
  const memberStartDate =
    memberDetails.data &&
    memberDetails.data.member &&
    memberDetails.data.member.startedAt
      ? new Date(memberDetails.data.member.startedAt)
      : null;
  const isLoading =
    memberDetails.apiStatus === ApiStatus.LOADING ||
    memberDetails.apiStatus === ApiStatus.IDLE;
  const setPageTitle = useTitle();

  useEffect(() => {
    if (memberDetails.data) {
      setPageTitle(
        `${memberDetails.data.member.fullName} - ${PageTitle.EVALUATIONS}`,
      );
    }
    logEvent('member_evaluations_page_visit');
  }, [setPageTitle, memberDetails.data]);

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', tablet: 'row' }}
        alignItems={{ xs: 'flex-start', tablet: 'center' }}
        gap={{ xs: 0.5, tablet: 6 }}
        pb={2}
        px={{ tablet: 1 }}
        justifyContent="start"
      >
        <Typography variant="h2" typography={{ xs: 'h3', tablet: 'h2' }}>
          Evaluation history
        </Typography>
        {!isLoading &&
          (hasPermission(permissions, 'P:R') ? (
            <Typography variant="body2" color="text.secondary">
              {evaluations.length - 1 > 0 ? evaluations.length - 1 : 'No'}{' '}
              evaluation{evaluations.length == 1 ? '' : 's'} while working on
              your project
            </Typography>
          ) : (
            <></>
          ))}
      </Box>
      <Divider sx={{ mx: -2, display: { xs: 'none', tablet: 'block' } }} />
      {isLoading ? (
        <Stack
          minHeight={520}
          direction={{ xs: 'column', tablet: 'row' }}
          mt={2}
          gap={{ xs: 1, tablet: 4 }}
          flexWrap="wrap"
          alignItems="start"
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              sx={{
                width: { xs: '100%', tablet: 200 },
                height: { xs: 79, tablet: 120 },
              }}
            />
          ))}
        </Stack>
      ) : (
        <Box minHeight={520}>
          <Box
            display="flex"
            flexWrap="wrap"
            mt={2}
            sx={{
              gap: { xs: 1, tablet: 2 },
              alignItems: 'flex-start',
            }}
          >
            {evaluations.map((e, idx) => (
              <Box key={idx} sx={tileBoxSx}>
                <EvaluationTile
                  providedByName={e.providedBy ? e.providedBy.fullName : '-'}
                  monthLabel={format(new Date(e.evaluationDate), 'MMMM yyyy')}
                  feedbackSummary={e.feedback?.qualityOfWork}
                  status={e.status}
                  evaluationId={e.id}
                  isLocked={e.status !== 'upcoming' && e.providedBy && !e.isOwn}
                />
              </Box>
            ))}

            {memberStartDate && (
              <Box sx={tileBoxSx}>
                <EvaluationTile
                  monthLabel={
                    memberStartDate ? format(memberStartDate, 'MMMM yyyy') : ''
                  }
                  status="cancelled"
                  isJoinEvent
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
